/**
 * Organization Password Policy の oRPC Contract（FE 先行定義）。
 *
 * SSOT は Backend の `password-policy.api-contract`。
 * `@standandforce/api-contract` に publish されたら、この local contract を
 * パッケージからの re-export に差し替える（`login-method-policy.ts` と同様）。
 *
 * @see https://github.com/standandforce/dresscode-backend/pull/8060
 */
import { oc } from '@orpc/contract';
import { z } from 'zod';

/** DRESS CODE が設定可能としているパスワード最小文字数の範囲。 */
export const PASSWORD_POLICY_MIN_LENGTH_RANGE = {
  min: 8,
  max: 128
} as const;

/** ポリシー要件（強度）のみ。表示・編集で共通に使う。 */
export const PasswordPolicyValueSchema = z.object({
  minLength: z.number().int().min(PASSWORD_POLICY_MIN_LENGTH_RANGE.min).max(PASSWORD_POLICY_MIN_LENGTH_RANGE.max),
  requireUppercase: z.boolean(),
  requireLowercase: z.boolean(),
  requireNumber: z.boolean(),
  requireSymbol: z.boolean(),
  leakedPasswordCheckEnabled: z.boolean()
});

/** 現在有効版 / 予約版の 1 件を表す表示用ビュー。 */
export const PasswordPolicyViewSchema = PasswordPolicyValueSchema.extend({
  /** 適用（発効）日時（ISO8601）。デフォルト表示は null。 */
  enforceFrom: z.string().datetime().nullable(),
  /** 永続化行が存在する場合のみ。デフォルト表示は null。 */
  updatedAt: z.string().datetime().nullable(),
  /** 永続化行がなく DRESS CODE 標準設定を表示するとき true。 */
  isDefault: z.boolean()
});

/**
 * DRESS CODE 推奨水準（適合度メーター / 推奨未達警告用）。
 * 編集値と同じ形状で返し、項目ごとの差分表示に使う。
 */
export const PasswordPolicyRecommendedSchema = PasswordPolicyValueSchema;

/**
 * 設定画面向けの合成ビュー。
 * current = 今強制中 / scheduled = 未発効の予約（0〜1 件）。
 */
export const PasswordPolicySettingsSchema = z.object({
  current: PasswordPolicyViewSchema,
  scheduled: PasswordPolicyViewSchema.nullable(),
  recommended: PasswordPolicyRecommendedSchema
});
export const PasswordPolicyVersionStatusSchema = z.enum(['active', 'scheduled', 'superseded']);
export const PasswordPolicyVersionSchema = PasswordPolicyValueSchema.extend({
  enforceFrom: z.string().datetime(),
  createdAt: z.string().datetime(),
  status: PasswordPolicyVersionStatusSchema
});
export const ListPasswordPolicyVersionsInputSchema = z.object({
  // query string 由来のため coerce（BE の password-policy.api-contract に合わせる）
  limit: z.coerce.number().int().min(1).max(50).default(10)
});
export const ListPasswordPolicyVersionsOutputSchema = z.object({
  versions: z.array(PasswordPolicyVersionSchema)
});
export const UpsertOrganizationPasswordPolicyInputSchema = z.object({
  minLength: z.number().int().min(PASSWORD_POLICY_MIN_LENGTH_RANGE.min).max(PASSWORD_POLICY_MIN_LENGTH_RANGE.max),
  requireUppercase: z.boolean(),
  requireLowercase: z.boolean(),
  requireNumber: z.boolean(),
  requireSymbol: z.boolean(),
  leakedPasswordCheckEnabled: z.boolean(),
  /** 適用（発効）日時（ISO8601）。省略時は即時適用。 */
  enforceFrom: z.string().datetime().optional()
});
export const passwordPolicyContract = oc.router({
  getOrgPolicy: oc.route({
    method: 'GET',
    path: '/v2/settings/password-policy'
  }).input(z.object({})).output(PasswordPolicySettingsSchema),
  upsertOrgPolicy: oc.route({
    method: 'PATCH',
    path: '/v2/settings/password-policy'
  }).input(UpsertOrganizationPasswordPolicyInputSchema).output(PasswordPolicySettingsSchema),
  listOrgPolicyVersions: oc.route({
    method: 'GET',
    path: '/v2/settings/password-policy/versions'
  }).input(ListPasswordPolicyVersionsInputSchema).output(ListPasswordPolicyVersionsOutputSchema)
});
export type PasswordPolicyValue = z.infer<typeof PasswordPolicyValueSchema>;
export type PasswordPolicyView = z.infer<typeof PasswordPolicyViewSchema>;
export type PasswordPolicySettings = z.infer<typeof PasswordPolicySettingsSchema>;
export type PasswordPolicyRecommended = z.infer<typeof PasswordPolicyRecommendedSchema>;
export type PasswordPolicyVersion = z.infer<typeof PasswordPolicyVersionSchema>;
export type UpsertOrganizationPasswordPolicyInput = z.infer<typeof UpsertOrganizationPasswordPolicyInputSchema>;

/** 推奨水準を下回っているか（推奨が true の項目のみ判定）。 */
export function isBelowRecommended(value: PasswordPolicyValue, recommended: PasswordPolicyRecommended): boolean {
  return value.minLength < recommended.minLength || recommended.requireUppercase && !value.requireUppercase || recommended.requireLowercase && !value.requireLowercase || recommended.requireNumber && !value.requireNumber || recommended.requireSymbol && !value.requireSymbol || recommended.leakedPasswordCheckEnabled && !value.leakedPasswordCheckEnabled;
}